import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FiShield as Shield, FiUsers as Users, FiPlus as Plus, FiEdit2 as Edit, FiTrash2 as Trash, FiChevronDown as ChevronDown, FiChevronUp as ChevronUp } from 'react-icons/fi';
import { apiCall } from '../../../../services/realApi';
import { Modal, Input, Button } from '../../../../components/ui';
import { PERMISSIONS, MODULES, moduleDescriptions } from '../../../../utils/permissions';

interface RolePermission {
  id: string;
  name: string;
  description?: string;
  resource?: string;
  action?: string;
}

export interface Role {
  id: string;
  name: string;
  description?: string;
  is_system_role?: boolean;
  user_count?: number;
  permissions?: (RolePermission | string)[];
}

interface RolesTabProps {
  roles: Role[];
  loading: boolean;
  onRefresh?: () => void;
}

const RolesTab: React.FC<RolesTabProps> = ({ roles, loading, onRefresh }) => {
  const [allPermissions, setAllPermissions] = useState<RolePermission[]>([]);
  const [permissionsLoading, setPermissionsLoading] = useState(false);
  const [permissionsError, setPermissionsError] = useState<string | null>(null);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [editingRole, setEditingRole] = useState<Role | null>(null);
  const [saving, setSaving] = useState(false);
  const [roleForm, setRoleForm] = useState<{
    name: string;
    description: string;
    permissionIds: string[];
  }>({
    name: '',
    description: '',
    permissionIds: []
  });
  const [expandedPermissionModules, setExpandedPermissionModules] = useState<Record<string, boolean>>({});

  const hasApiRoles = roles.length > 0;

  const loadPermissions = useCallback(async () => {
    if (!hasApiRoles) return;
    setPermissionsLoading(true);
    setPermissionsError(null);
    try {
      const data = await apiCall('/roles/permissions');
      setAllPermissions(Array.isArray(data) ? data : []);
    } catch (err: unknown) {
      console.error('Error loading permissions:', err);
      setAllPermissions([]);
      setPermissionsError((err instanceof Error ? err.message : null) || 'Failed to load permissions');
    } finally {
      setPermissionsLoading(false);
    }
  }, [hasApiRoles]);

  useEffect(() => {
    loadPermissions();
  }, [loadPermissions]);

  const permissionModuleOrder = [
    MODULES.HUB,
    MODULES.PLATFORMOPS,
    MODULES.DATAOPS,
    MODULES.GENAIOPS,
    MODULES.AGENTOPS
  ];

  const moduleLabels = {
    [MODULES.HUB]: 'Hub',
    [MODULES.PLATFORMOPS]: 'CloudOps',
    [MODULES.DATAOPS]: 'DataOps',
    [MODULES.GENAIOPS]: 'GenAIOps',
    [MODULES.AGENTOPS]: 'AgentOps'
  };

  const getPermissionModule = (permission: RolePermission) => {
    const name = permission?.name || '';
    const resource = name.split('.')[0];

    switch (resource) {
      case 'platformops':
        return MODULES.PLATFORMOPS;
      case 'dataops':
        return MODULES.DATAOPS;
      case 'genaiops':
        return MODULES.GENAIOPS;
      case 'agentops':
        return MODULES.AGENTOPS;
      default:
        return MODULES.HUB;
    }
  };

  const groupedPermissions = useMemo(() => {
    return allPermissions.reduce<Record<string, RolePermission[]>>((acc, permission) => {
      const moduleKey = getPermissionModule(permission);
      if (!acc[moduleKey]) {
        acc[moduleKey] = [];
      }
      acc[moduleKey].push(permission);
      return acc;
    }, {});
  }, [allPermissions]);

  const getRoleColor = (roleName: string | undefined) => {
    const name = roleName?.toLowerCase() || '';
    if (name.includes('partner')) return 'blue';
    if (name.includes('client')) return 'green';
    if (name.includes('aws')) return 'purple';
    if (name.includes('aivilo') || name.includes('staff')) return 'gray';
    return 'blue';
  };

  const getColorClasses = (color: string) => {
    const colors = {
      blue: {
        badge: 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-300',
        icon: 'text-blue-500'
      },
      green: {
        badge: 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300',
        icon: 'text-green-500'
      },
      purple: {
        badge: 'bg-purple-100 text-purple-800 dark:bg-purple-900/50 dark:text-purple-300',
        icon: 'text-purple-500'
      },
      gray: {
        badge: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
        icon: 'text-gray-500'
      }
    };
    return colors[color as keyof typeof colors] || colors.blue;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  const openCreateRole = () => {
    setEditingRole(null);
    setRoleForm({
      name: '',
      description: '',
      permissionIds: []
    });
    setShowRoleModal(true);
  };

  const openEditRole = (role: Role) => {
    const permissionIds = Array.isArray(role.permissions)
      ? role.permissions.map((perm) => String(typeof perm === 'string' ? perm : perm.id))
      : [];
    setEditingRole(role);
    setRoleForm({
      name: role.name || '',
      description: role.description || '',
      permissionIds
    });
    setShowRoleModal(true);
  };

  const closeRoleModal = () => {
    if (saving) return;
    setShowRoleModal(false);
    setEditingRole(null);
    setRoleForm({
      name: '',
      description: '',
      permissionIds: []
    });
    setExpandedPermissionModules({});
  };

  const handlePermissionToggle = (permissionId: string) => {
    setRoleForm((prev) => {
      const current = prev.permissionIds || [];
      const exists = current.includes(permissionId);
      return {
        ...prev,
        permissionIds: exists ? current.filter((id) => id !== permissionId) : [...current, permissionId]
      };
    });
  };

  const handleSelectAllPermissions = () => {
    setRoleForm((prev) => ({
      ...prev,
      permissionIds: allPermissions.map((perm) => String(perm.id))
    }));
  };

  const handleDeselectAllPermissions = () => {
    setRoleForm((prev) => ({ ...prev, permissionIds: [] }));
  };

  const togglePermissionModule = (moduleKey: string) => {
    setExpandedPermissionModules((prev) => ({
      ...prev,
      [moduleKey]: !prev[moduleKey]
    }));
  };

  const handleSaveRole = async () => {
    if (!roleForm.name.trim()) {
      alert('Role name is required');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: roleForm.name.trim(),
        description: roleForm.description?.trim() || null,
        permission_ids: roleForm.permissionIds
      };

      if (editingRole?.id) {
        await apiCall(`/roles/${editingRole.id}`, {
          method: 'PUT',
          body: JSON.stringify(payload)
        });
      } else {
        await apiCall('/roles', {
          method: 'POST',
          body: JSON.stringify(payload)
        });
      }

      closeRoleModal();
      if (onRefresh) {
        onRefresh();
      }
      loadPermissions();
    } catch (err: unknown) {
      console.error('Error saving role:', err);
      alert((err instanceof Error ? err.message : null) || 'Failed to save role');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteRole = async (role: Role) => {
    if (role.is_system_role) {
      alert('System roles cannot be deleted.');
      return;
    }
    if (!globalThis.confirm(`Delete role "${role.name}"? This cannot be undone.`)) return;
    try {
      await apiCall(`/roles/${role.id}`, { method: 'DELETE' });
      if (onRefresh) {
        onRefresh();
      }
      loadPermissions();
    } catch (err: unknown) {
      console.error('Error deleting role:', err);
      alert((err instanceof Error ? err.message : null) || 'Failed to delete role');
    }
  };

  // If no roles from API, show default roles based on the screenshot
  const displayRoles = roles.length > 0 ? roles : [
    {
      id: '1',
      name: 'AWS Partner',
      description: 'Full access to partner workspace and client projects',
      user_count: 3,
      permissions: [
        PERMISSIONS.PROJECTS_VIEW,
        PERMISSIONS.PROJECTS_EDIT,
        PERMISSIONS.PROJECTS_CREATE,
        PERMISSIONS.FINANCE_VIEW,
        PERMISSIONS.FINANCE_EDIT,
        PERMISSIONS.TIMESHEET_MANAGE,
        PERMISSIONS.ASSESSMENTS_MANAGE,
        PERMISSIONS.INTEGRATIONS_MANAGE
      ]
    },
    {
      id: '2',
      name: 'Client User',
      description: 'View-only access to assigned projects',
      user_count: 1,
      permissions: [
        PERMISSIONS.PROJECTS_VIEW,
        PERMISSIONS.FINANCE_VIEW,
        PERMISSIONS.TIMESHEET_VIEW,
        PERMISSIONS.ASSESSMENTS_VIEW
      ]
    },
    {
      id: '3',
      name: 'AWS Staff',
      description: 'Administrative access across all partners and projects',
      user_count: 1,
      permissions: [
        PERMISSIONS.ADMIN_VIEW,
        PERMISSIONS.PROJECTS_MANAGE,
        PERMISSIONS.FINANCE_MANAGE,
        PERMISSIONS.USERS_MANAGE,
        PERMISSIONS.PARTNERS_MANAGE,
        PERMISSIONS.ASSESSMENTS_MANAGE,
        PERMISSIONS.INTEGRATIONS_MANAGE,
        PERMISSIONS.PLATFORMOPS_MANAGE
      ]
    },
    {
      id: '4',
      name: 'Aivilo Staff',
      description: 'System administrative access',
      user_count: 0,
      permissions: [PERMISSIONS.SYSTEM_ADMIN]
    }
  ];

  return (
    <div className="space-y-6">
      {hasApiRoles && (
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Roles</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">Manage role permissions and access</p>
          </div>
          <Button variant="primary" onClick={openCreateRole} className="flex items-center space-x-2">
            <Plus className="w-4 h-4" />
            <span>Add Role</span>
          </Button>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {displayRoles.map((role) => {
        const color = getRoleColor(role.name);
        const colors = getColorClasses(color);
        const permissions = role.permissions || [];

        return (
          <div
            key={role.id || role.name}
            className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 transition-colors"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div className={`${colors.icon}`}>
                  <Shield className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white transition-colors">
                    {role.name}
                  </h3>
                  {role.is_system_role && (
                    <p className="text-xs text-gray-500 dark:text-gray-400">System role</p>
                  )}
                </div>
              </div>
              {hasApiRoles && (
                <div className="flex items-center space-x-2">
                  <Button variant="secondary" size="sm" onClick={() => openEditRole(role)}>
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button variant="danger" size="sm" onClick={() => handleDeleteRole(role)} disabled={role.is_system_role}>
                    <Trash className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </div>

            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4 transition-colors">
              {role.description || 'No description available'}
            </p>

            <div className="flex items-center text-sm text-gray-600 dark:text-gray-400 mb-4 transition-colors">
              <Users className="w-4 h-4 mr-2" />
              <span>{role.user_count || 0} {role.user_count === 1 ? 'user' : 'users'}</span>
            </div>

            <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2 uppercase tracking-wider transition-colors">
                Permissions
              </p>
              <div className="flex flex-wrap gap-2">
                {permissions.length > 0 ? (
                  permissions.map((permission: RolePermission | string) => (
                    <span
                      key={typeof permission === 'string' ? permission : permission.name}
                      className="inline-flex items-center px-2 py-1 rounded text-xs font-mono bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 transition-colors"
                    >
                      {typeof permission === 'string' ? permission : permission.name}
                    </span>
                  ))
                ) : (
                  <span className="text-xs text-gray-500 dark:text-gray-400">No permissions assigned</span>
                )}
              </div>
            </div>
          </div>
        );
      })}
      </div>

      {showRoleModal && (
        <Modal
          isOpen={showRoleModal}
          onClose={closeRoleModal}
          title={editingRole ? 'Edit Role' : 'Create Role'}
          maxWidth="max-w-3xl"
        >
          <div className="space-y-4">
            <Input
              label="Role Name"
              value={roleForm.name}
              onChange={(e) => setRoleForm((prev) => ({ ...prev, name: e.target.value }))}
              required
            />
            <Input
              label="Description"
              value={roleForm.description}
              onChange={(e) => setRoleForm((prev) => ({ ...prev, description: e.target.value }))}
            />

            <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="text-sm font-medium text-gray-900 dark:text-white">Permissions</h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {roleForm.permissionIds.length} selected
                  </p>
                </div>
                <div className="space-x-2">
                  <button
                    type="button"
                    onClick={handleSelectAllPermissions}
                    className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                  >
                    Select All
                  </button>
                  <button
                    type="button"
                    onClick={handleDeselectAllPermissions}
                    className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                  >
                    Deselect All
                  </button>
                </div>
              </div>

              {permissionsLoading ? (
                <div className="flex items-center justify-center py-6">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                </div>
              ) : permissionsError ? (
                <p className="text-sm text-red-600 dark:text-red-400">{permissionsError}</p>
              ) : (
                <div className="space-y-2 max-h-72 overflow-y-auto">
                  {permissionModuleOrder.map((moduleKey) => {
                    const modulePermissions = groupedPermissions[moduleKey] || [];
                    if (modulePermissions.length === 0) {
                      return null;
                    }

                    const selectedCount = modulePermissions.filter((permission) =>
                      roleForm.permissionIds.includes(String(permission.id))
                    ).length;

                    return (
                      <div key={moduleKey} className="border border-gray-200 dark:border-gray-700 rounded-md">
                        <button
                          type="button"
                          onClick={() => togglePermissionModule(moduleKey)}
                          className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-800 rounded-md"
                        >
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center">
                              <span className="text-sm font-medium text-gray-900 dark:text-white">
                                {(moduleLabels as Record<string, string>)[moduleKey] || moduleKey}
                              </span>
                              <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">
                                {selectedCount} of {modulePermissions.length}
                              </span>
                            </div>
                            {moduleDescriptions[moduleKey] && (
                              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                {moduleDescriptions[moduleKey]}
                              </p>
                            )}
                          </div>
                          {expandedPermissionModules[moduleKey] ? (
                            <ChevronUp className="w-4 h-4 text-gray-500" />
                          ) : (
                            <ChevronDown className="w-4 h-4 text-gray-500" />
                          )}
                        </button>

                        {expandedPermissionModules[moduleKey] && (
                          <div className="px-2 pb-2 space-y-1">
                            {modulePermissions.map((permission) => (
                              <label
                                key={permission.id}
                                className="flex items-start space-x-2 p-2 hover:bg-gray-50 dark:hover:bg-gray-800 rounded cursor-pointer"
                              >
                                <input
                                  type="checkbox"
                                  checked={roleForm.permissionIds.includes(String(permission.id))}
                                  onChange={() => handlePermissionToggle(String(permission.id))}
                                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 mt-0.5"
                                />
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center flex-wrap gap-x-2">
                                    <span className="text-sm text-gray-900 dark:text-white">
                                      {permission.name}
                                    </span>
                                    {!permission.description && permission.resource && permission.action && (
                                      <span className="text-xs text-gray-500 dark:text-gray-400">
                                        ({permission.resource}.{permission.action})
                                      </span>
                                    )}
                                  </div>
                                  {permission.description && (
                                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                      {permission.description}
                                    </p>
                                  )}
                                </div>
                              </label>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
              <Button variant="secondary" onClick={closeRoleModal} disabled={saving}>
                Cancel
              </Button>
              <Button variant="primary" onClick={handleSaveRole} loading={saving} disabled={saving || permissionsLoading}>
                Save Role
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default RolesTab;

